/**
 * Dashboard Page Metadata
 * - title: Header title shown on the left (also used for browser tab)
 * - description: Subtitle shown under title
 * - breadcrumbs: Optional manual crumbs
 *
 * NOTES:
 * - Prefix matching is intentional for dynamic routes.
 * - Add new routes freely; no refactors required elsewhere.
 */

export const dashboardPageMeta = {
  /* ===================== ADMIN MAIN ===================== */

  "/admin/dashboard": {
    title: "Admin Dashboard",
    description: "Platform overview and executive briefing",
    breadcrumbs: [{ label: "Admin", to: "/admin/dashboard" }],
  },

  "/admin/products": {
    title: "Product Manager",
    description: "Manage and deploy platform assets, inventory, and services",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Products", to: "/admin/products" },
    ],
  },

  "/admin/products/edit": {
    title: "Edit Product",
    description: "Modify technical metadata and deployment settings",
    breadcrumbs: [
      { label: "Products", to: "/admin/products" },
      { label: "Edit", to: null },
    ],
  },

  "/admin/training/mentors": {
    title: "Mentor Assignments",
    description: "Manage mentor-student relationships",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Mentors", to: null },
    ],
  },

  /* ===================== ADMIN CLIENTS ===================== */

  "/admin/clients": {
    title: "Client Management",
    description: "View and manage all platform users",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Clients", to: "/admin/clients" },
    ],
  },

  "/admin/clients/": {
    title: "Client Details",
    description: "Individual client profile and activity",
    breadcrumbs: [
      { label: "Clients", to: "/admin/clients" },
      { label: "Details", to: null },
    ],
  },

  /* ===================== ADMIN CHECKLISTS ===================== */

  "/admin/checklist": {
    title: "Course Checklists",
    description: "Track required tasks before publishing learning tools",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Checklists", to: "/admin/checklist" },
    ],
  },

  /* ===================== ADMIN MARKETING ===================== */

  "/admin/marketing": {
    title: "Marketing Dashboard",
    description: "Campaign analytics and lead management",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Marketing", to: "/admin/marketing" },
    ],
  },

  "/admin/marketing/leads": {
    title: "Marketing Leads",
    description: "Manage and nurture leads",
    breadcrumbs: [
      { label: "Marketing", to: "/admin/marketing" },
      { label: "Leads", to: null },
    ],
  },

  "/admin/marketing/emails": {
    title: "Email Campaigns",
    description: "Email marketing management",
    breadcrumbs: [
      { label: "Marketing", to: "/admin/marketing" },
      { label: "Emails", to: null },
    ],
  },

  "/admin/marketing/landing": {
    title: "Landing Pages",
    description: "Manage marketing landing pages",
    breadcrumbs: [
      { label: "Marketing", to: "/admin/marketing" },
      { label: "Landing", to: null },
    ],
  },

  "/admin/marketing/announcements": {
    title: "Announcements",
    description: "Send announcements and view notification history",
    breadcrumbs: [
      { label: "Marketing", to: "/admin/marketing" },
      { label: "Announcements", to: null },
    ],
  },

  /* ===================== ADMIN FINANCES ===================== */

  "/admin/finances": {
    title: "Finances",
    description: "Revenue, expenses, and financial reports",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Finances", to: "/admin/finances" },
    ],
  },

  "/admin/site-statistics": {
    title: "Site Statistics",
    description: "Product enrollment and learner progress metrics",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Site Statistics", to: "/admin/site-statistics" },
    ],
  },

  /* ===================== ADMIN CONTRACTS ===================== */

  "/admin/contracts": {
    title: "Contracts Dashboard",
    description: "AI insights, action items, and bid decisions for government contracts",
    breadcrumbs: [
      { label: "Contracts", to: "/admin/contracts" },
    ],
  },

  "/admin/partners": {
    title: "Partners",
    description: "Subcontractor partners for contract opportunities",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Partners", to: "/admin/partners" },
    ],
  },

  "/admin/outreach": {
    title: "Outreach Log",
    description: "Every email, call, and form sent to partners",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Outreach Log", to: "/admin/outreach" },
    ],
  },

  "/admin/opportunities": {
    title: "Opportunities",
    description: "Contract opportunities and their linked partners",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Opportunities", to: "/admin/opportunities" },
    ],
  },

  "/admin/inbox": {
    title: "Contracts Inbox",
    description: "Partner, government, and SAM.gov email to contact@, summarized",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Contracts Inbox", to: "/admin/inbox" },
    ],
  },

  "/admin/sam-scan": {
    title: "SAM.gov Scan",
    description: "New SAM.gov notices for your NAICS codes, award alerts, and your registration",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "SAM.gov Scan", to: "/admin/sam-scan" },
    ],
  },

  "/admin/sam-status": {
    title: "SAM.gov Status",
    description: "Partner SAM.gov registrations and who qualifies for a position",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "SAM.gov Status", to: "/admin/sam-status" },
    ],
  },

  "/admin/work-board": {
    title: "Work Board",
    description: "Partners, open quote requests, and quotes by type of work",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Work Board", to: "/admin/work-board" },
    ],
  },

  /* ===================== ADMIN COACHING ===================== */

  /* ===================== ADMIN MESSAGES ===================== */

  "/admin/messages": {
    title: "Admin Inbox",
    description: "View and respond to user messages",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Messages", to: "/admin/messages" },
    ],
  },

  /* ===================== ADMIN EMPLOYEES ===================== */

  "/admin/employees": {
    title: "Team Management",
    description: "Manage team members and roles",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Team", to: "/admin/employees" },
    ],
  },

  /* ===================== ADMIN EMAIL SETTINGS ===================== */

  "/admin/email/emailsettings": {
    title: "Email Settings",
    description: "Configure email templates and settings",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Email Settings", to: null },
    ],
  },

  /* ===================== ADMIN SYSTEM ===================== */

  "/admin/ScopeDataStudio": {
    title: "Data Studio",
    description: "Database management and exploration",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Data Studio", to: "/admin/ScopeDataStudio" },
    ],
  },

  "/admin/system/overview": {
    title: "System Overview",
    description: "Platform health and status",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "System", to: null },
    ],
  },

  "/admin/system/stripe-tester": {
    title: "Stripe Tester",
    description: "Test payment integrations",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Stripe Tester", to: null },
    ],
  },

  "/admin/exams/bulk-upload": {
    title: "Exam Bulk Upload",
    description: "Upload exam questions in bulk",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Bulk Upload", to: null },
    ],
  },

  /* ===================== ADMIN SIMULATIONS ===================== */

  /* ===================== ADMIN OTHER ===================== */

  "/admin/profile": {
    title: "Admin Profile",
    description: "Your admin account settings",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Profile", to: null },
    ],
  },

  "/admin/scheduling": {
    title: "Scheduling",
    description: "Calendar and appointment management",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Scheduling", to: null },
    ],
  },

  "/admin/intake/add": {
    title: "Add Intake Form",
    description: "Create client intake forms",
    breadcrumbs: [
      { label: "Admin", to: "/admin/dashboard" },
      { label: "Intake Form", to: null },
    ],
  },

  /* ===================== CLIENT DASHBOARD ===================== */

  "/learner/dashboard": {
    title: "My Dashboard",
    description: "Your learning activity, progress, and next steps",
    breadcrumbs: [{ label: "Dashboard", to: "/learner/dashboard" }],
  },

  "/academy/my": {
    title: "TheScope180 Academy",
    description: "Every full course you're enrolled in, in one place",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "TheScope180 Academy", to: null },
    ],
  },

  "/academy/my/pmbok8-exam-prep": {
    title: "PMBOK 8 Contact Hours",
    description: "35 contact hours for the July 2026 PMP exam",
    breadcrumbs: [
      { label: "TheScope180 Academy", to: "/academy/my" },
      { label: "PMBOK 8 Contact Hours", to: null },
    ],
  },

  "/academy/my/prompt-engineering": {
    title: "Prompt Engineering Mastery",
    description: "Master the art and science of AI prompting",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Prompt Engineering", to: null },
    ],
  },

  "/academy/my/ai-systems-delivery": {
    title: "AI Systems Delivery",
    description: "A Technical Playbook for Project Managers in Transition",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "AI Systems Delivery", to: null },
    ],
  },

  "/learner/my-exams": {
    title: "My Exams",
    description: "Your purchased practice exams and simulations",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "My Exams", to: "/learner/my-exams" },
    ],
  },

  "/learner/my-learning-tools": {
    title: "My Learning Tools",
    description: "Free learning tools and video content",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "My Learning Tools", to: "/learner/my-learning-tools" },
    ],
  },

  "/learner/my-courses": {
    title: "My Courses",
    description: "Your enrolled courses",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Courses", to: "/learner/my-courses" },
    ],
  },


  "/learner/downloads": {
    title: "My Downloads",
    description: "Your digital products and resources",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Downloads", to: "/learner/downloads" },
    ],
  },

  "/learner/profile": {
    title: "My Profile",
    description: "Your account settings and preferences",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Profile", to: "/learner/profile" },
    ],
  },

  "/subscription/manage": {
    title: "Manage Sub",
    description: "View and manage your TheScope180 membership",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Profile", to: "/learner/profile" },
      { label: "Manage Sub", to: "/subscription/manage" },
    ],
  },

  "/learner/support": {
    title: "Support",
    description: "Get help and chat with our AI assistant",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Support", to: "/learner/support" },
    ],
  },

  "/learner/resume-checkout": {
    title: "Resume Checkout",
    description: "Complete your pending purchase",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Checkout", to: null },
    ],
  },

  "/learner/reader": {
    title: "E-Book Reader",
    description: "Read your digital content",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Reader", to: null },
    ],
  },

  "/admin/pmbok8-time-log": {
    title: "PMBOK 8 Time Log",
    description: "Server record of each learner's contact hours",
  },

  "/admin/learner-activity": {
    title: "Learner Activity",
    description: "What signed-in learners did",
    breadcrumbs: [
      { label: "Dashboard", to: "/admin/dashboard" },
      { label: "Learner Activity", to: null },
    ],
  },

  "/learner/practice": {
    title: "Daily Practice",
    description: "Ten minutes a day: review, quiz, connect",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Daily Practice", to: null },
    ],
  },

  "/learner/my-progress": {
    title: "My Progress",
    description: "Track your learning journey",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Progress", to: null },
    ],
  },

  "/learner/course-progress": {
    title: "Course Progress",
    description: "Detailed course progress tracking",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Course Progress", to: null },
    ],
  },

  "/learner/learning-experiences": {
    title: "Learning Experience",
    description: "Interactive learning content",
    breadcrumbs: [
      { label: "My Exams", to: "/learner/my-exams" },
      { label: "Experience", to: null },
    ],
  },

  "/learner/exam-results": {
    title: "Exam Results",
    description: "Your assessment performance",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Results", to: null },
    ],
  },

  "/learner/skill-builders": {
    title: "Skill Builders",
    description: "Practice tools aligned to PMBOK 8 and the July 2026 ECO",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
    ],
  },

  "/learner/flashcards": {
    title: "Flashcard Study",
    description: "Master key concepts with interactive flashcards",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Flashcards", to: "/learner/flashcards" },
    ],
  },

  "/learner/flashcards/": {
    title: "Flashcard Study",
    description: "Study session in progress",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Flashcards", to: "/learner/flashcards" },
      { label: "Study", to: null },
    ],
  },

  "/learner/concept-mapper": {
    title: "Concept Mapper",
    description: "Visualize relationships between PM concepts",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Concept Mapper", to: "/learner/concept-mapper" },
    ],
  },

  "/learner/concept-mapper/": {
    title: "Concept Map",
    description: "Exploring concept relationships",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Concept Mapper", to: "/learner/concept-mapper" },
      { label: "Map", to: null },
    ],
  },

  "/learner/quick-quiz": {
    title: "Quick Quiz",
    description: "Test your knowledge with timed quizzes",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Quick Quiz", to: "/learner/quick-quiz" },
    ],
  },

  "/learner/quick-quiz/play": {
    title: "Quick Quiz",
    description: "Quiz in progress",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Quick Quiz", to: "/learner/quick-quiz" },
      { label: "Play", to: null },
    ],
  },

  "/learner/practice-scenarios": {
    title: "Practice Scenarios",
    description: "Apply your knowledge to realistic project situations",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Practice Scenarios", to: "/learner/practice-scenarios" },
    ],
  },

  "/learner/practice-scenarios/play": {
    title: "Practice Scenario",
    description: "Scenario in progress",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skill Builders", to: "/learner/skill-builders" },
      { label: "Practice Scenarios", to: "/learner/practice-scenarios" },
      { label: "Play", to: null },
    ],
  },

  /* ===================== ASSESSMENTS ===================== */

  "/assessments/start": {
    title: "Skills Assessment",
    description: "Begin your personalized diagnostic and learning path",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Assessment", to: "/assessments/start" },
    ],
  },

  "/diagnostic": {
    title: "Skills Diagnostic",
    description: "60 questions, readiness estimate, results by ECO task",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Skills Diagnostic", to: null },
    ],
  },

  "/learner/readiness-course": {
    title: "PMP Readiness Course",
    description: "Your course, built from your Skills Diagnostic",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Readiness Course", to: null },
    ],
  },

  "/assessments": {
    title: "Assessment",
    description: "Complete your skills diagnostic",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Assessment", to: null },
    ],
  },

  /* ===================== LEARNING ===================== */

  "/learn": {
    title: "Learning",
    description: "Interactive learning experience",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Learn", to: null },
    ],
  },

  "/learn/pmp-foundations-exam-overview": {
    title: "PMBOK 8 Foundations",
    description: "PMBOK 8 Foundations & Exam Overview — Free course",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "My Courses", to: "/learner/my-courses" },
      { label: "PMP Foundations", to: null },
    ],
  },

  "/learner/generated-course": {
    title: "Personal Learning Plan",
    description: "Your AI-generated personalized learning path",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "My Courses", to: "/learner/my-courses" },
      { label: "Personal Learning Plan", to: null },
    ],
  },
  "/learner/my-generated-courses": {
    title: "My Learning Plans",
    description: "Your personalized AI-generated learning plans",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "My Learning Plans", to: null },
    ],
  },

  "/learn/agile-vs-hybrid": {
    title: "Agile vs Hybrid for Project Managers",
    description: "Mini course on Agile vs Hybrid project management approaches",
    breadcrumbs: [
      { label: "Products", to: "/products" },
      { label: "Agile vs Hybrid", to: null },
    ],
  },

  "/academy/my/project-management-101": {
    title: "PM101 Course Player",
    description: "Project Management 101 interactive course",
    breadcrumbs: [
      { label: "My Learning Tools", to: "/learner/my-learning-tools" },
      { label: "PM101", to: null },
    ],
  },

  "/learn/pmi-cpmai/player": {
    title: "PMI-CPMAI Course",
    description: "Certified Practitioner in Machine Learning and AI",
    breadcrumbs: [
      { label: "My Learning Tools", to: "/learner/my-learning-tools" },
      { label: "CPMAI", to: null },
    ],
  },

  "/learn/pm101/sim": {
    title: "PM Simulator",
    description: "Interactive project management simulation with real-world scenarios",
    breadcrumbs: [
      { label: "My Learning Tools", to: "/learner/my-learning-tools" },
      { label: "PM Simulator", to: null },
    ],
  },

  "/learn/oracle": {
    title: "Oracle EPM Course",
    description: "Implementing Oracle Enterprise Performance Management",
    breadcrumbs: [
      { label: "My Learning Tools", to: "/learner/my-learning-tools" },
      { label: "Oracle EPM", to: null },
    ],
  },

  "/learn/exams/pmp-practice": {
    title: "PMP Practice Exam",
    description: "PMP certification practice questions",
    breadcrumbs: [
      { label: "My Exams", to: "/learner/my-exams" },
      { label: "PMP Practice", to: null },
    ],
  },

  "/learn/exams/pmp-scenario": {
    title: "PMP Scenario Exam",
    description: "PMP scenario-based practice questions",
    breadcrumbs: [
      { label: "My Exams", to: "/learner/my-exams" },
      { label: "Scenario Exam", to: null },
    ],
  },

  /* ===================== MENTOR ===================== */

  "/mentor/dashboard": {
    title: "Mentor Dashboard",
    description: "Mentor coaching tools and learner support",
    breadcrumbs: [{ label: "Mentor", to: "/mentor/dashboard" }],
  },

  /* ===================== SUPPORT ===================== */

  "/support/dashboard": {
    title: "Support Dashboard",
    description: "Client support tools and service workflows",
    breadcrumbs: [{ label: "Support", to: "/support/dashboard" }],
  },

  /* ===================== INTAKE ===================== */

  "/dashboard/my-intake": {
    title: "My Intake Form",
    description: "Complete your onboarding questionnaire",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Intake", to: null },
    ],
  },

  /* ===================== DELIVERABLES SUITE ===================== */

  "/suite/deliverables": {
    title: "Deliverables Suite",
    description: "40+ AI-powered PM document generators — business cases, charters, budgets, and more",
    breadcrumbs: [
      { label: "Dashboard", to: "/learner/dashboard" },
      { label: "Deliverables Suite", to: "/suite/deliverables" },
    ],
  },
};
